import type { Pointer } from "../types/fluid-types.js"

export class PointerTracker {
	private pointers: Pointer[] = []
	private canvas: HTMLCanvasElement
	private onSplat?: (pointer: Pointer) => void
	private onInteractionStart?: (pointer: Pointer) => void
	private onInteractionEnd?: (pointer: Pointer) => void

	constructor(
		canvas: HTMLCanvasElement,
		callbacks: {
			onSplat?: (pointer: Pointer) => void
			onInteractionStart?: (pointer: Pointer) => void
			onInteractionEnd?: (pointer: Pointer) => void
		} = {}
	) {
		this.canvas = canvas
		this.onSplat = callbacks.onSplat
		this.onInteractionStart = callbacks.onInteractionStart
		this.onInteractionEnd = callbacks.onInteractionEnd

		// Initialize with one pointer
		this.pointers.push(this.createPointer())

		this.setupEventListeners()
	}

	private createPointer(): Pointer {
		return {
			id: -1,
			texcoordX: 0,
			texcoordY: 0,
			prevTexcoordX: 0,
			prevTexcoordY: 0,
			deltaX: 0,
			deltaY: 0,
			down: false,
			moved: false,
			color: [1, 0, 0], // Will be set by color system
		}
	}

	private setupEventListeners(): void {
		// Mouse events
		this.canvas.addEventListener("mousedown", this.handleMouseDown.bind(this))
		this.canvas.addEventListener("mousemove", this.handleMouseMove.bind(this))
		window.addEventListener("mouseup", this.handleMouseUp.bind(this))

		// Touch events
		this.canvas.addEventListener(
			"touchstart",
			this.handleTouchStart.bind(this),
			{ passive: false }
		)
		this.canvas.addEventListener("touchmove", this.handleTouchMove.bind(this), {
			passive: false,
		})
		window.addEventListener("touchend", this.handleTouchEnd.bind(this))
	}

	private handleMouseDown(e: MouseEvent): void {
		const posX = this.scaleByPixelRatio(e.offsetX)
		const posY = this.scaleByPixelRatio(e.offsetY)
		let pointer = this.pointers.find((p) => p.id === -1)
		if (!pointer) {
			pointer = this.createPointer()
			this.pointers.push(pointer)
		}
		this.updatePointerDown(pointer, -1, posX, posY)
	}

	private handleMouseMove(e: MouseEvent): void {
		const pointer = this.pointers[0]
		const posX = this.scaleByPixelRatio(e.offsetX)
		const posY = this.scaleByPixelRatio(e.offsetY)
		this.updatePointerMove(pointer, posX, posY)
	}

	private handleMouseUp(): void {
		this.updatePointerUp(this.pointers[0])
	}

	private handleTouchStart(e: TouchEvent): void {
		e.preventDefault()
		const touches = e.targetTouches
		for (let i = 0; i < touches.length; i++) {
			let pointer = this.pointers[i] || this.createPointer()
			if (i >= this.pointers.length) {
				this.pointers.push(pointer)
			}
			const posX = this.scaleByPixelRatio(touches[i].pageX)
			const posY = this.scaleByPixelRatio(touches[i].pageY)
			this.updatePointerDown(pointer, touches[i].identifier, posX, posY)
		}
	}

	private handleTouchMove(e: TouchEvent): void {
		e.preventDefault()
		const touches = e.targetTouches
		for (let i = 0; i < touches.length; i++) {
			const pointer = this.pointers.find((p) => p.id === touches[i].identifier)
			if (!pointer) continue
			const posX = this.scaleByPixelRatio(touches[i].pageX)
			const posY = this.scaleByPixelRatio(touches[i].pageY)
			this.updatePointerMove(pointer, posX, posY)
		}
	}

	private handleTouchEnd(e: TouchEvent): void {
		const touches = e.changedTouches
		for (let i = 0; i < touches.length; i++) {
			const pointer = this.pointers.find((p) => p.id === touches[i].identifier)
			if (!pointer) continue
			this.updatePointerUp(pointer)
		}
	}

	private updatePointerDown(
		pointer: Pointer,
		id: number,
		posX: number,
		posY: number
	): void {
		pointer.id = id
		pointer.down = true
		pointer.moved = false
		pointer.texcoordX = posX / this.canvas.width
		pointer.texcoordY = 1.0 - posY / this.canvas.height
		pointer.prevTexcoordX = pointer.texcoordX
		pointer.prevTexcoordY = pointer.texcoordY
		pointer.deltaX = 0
		pointer.deltaY = 0

		this.onInteractionStart?.(pointer)
	}

	private updatePointerMove(
		pointer: Pointer,
		posX: number,
		posY: number
	): void {
		pointer.prevTexcoordX = pointer.texcoordX
		pointer.prevTexcoordY = pointer.texcoordY
		pointer.texcoordX = posX / this.canvas.width
		pointer.texcoordY = 1.0 - posY / this.canvas.height
		pointer.deltaX = this.correctDelta(
			pointer.texcoordX - pointer.prevTexcoordX
		)
		pointer.deltaY = this.correctDelta(
			pointer.texcoordY - pointer.prevTexcoordY
		)
		pointer.moved = Math.abs(pointer.deltaX) > 0 || Math.abs(pointer.deltaY) > 0
	}

	private updatePointerUp(pointer: Pointer): void {
		pointer.down = false
		this.onInteractionEnd?.(pointer)
	}

	private scaleByPixelRatio(input: number): number {
		return input * (window.devicePixelRatio || 1)
	}

	private correctDelta(delta: number): number {
		const aspectRatio = this.canvas.width / this.canvas.height
		if (aspectRatio < 1) delta *= aspectRatio
		return delta
	}

	public updatePointers(velocityThreshold: number = 0.001): void {
		this.pointers.forEach((pointer) => {
			if (pointer.moved) {
				pointer.moved = false
				// Only create splat if mouse is moving with reasonable velocity
				const velocity = Math.sqrt(
					pointer.deltaX * pointer.deltaX + pointer.deltaY * pointer.deltaY
				)
				if (velocity > velocityThreshold) {
					this.onSplat?.(pointer)
				}
			}
		})
	}

	public setPointerColor(color: [number, number, number]): void {
		this.pointers.forEach((pointer) => {
			pointer.color = color
		})
	}

	public getPointers(): Pointer[] {
		return this.pointers
	}

	public destroy(): void {
		this.canvas.removeEventListener(
			"mousedown",
			this.handleMouseDown.bind(this)
		)
		this.canvas.removeEventListener(
			"mousemove",
			this.handleMouseMove.bind(this)
		)
		window.removeEventListener("mouseup", this.handleMouseUp.bind(this))
		this.canvas.removeEventListener(
			"touchstart",
			this.handleTouchStart.bind(this)
		)
		this.canvas.removeEventListener(
			"touchmove",
			this.handleTouchMove.bind(this)
		)
		window.removeEventListener("touchend", this.handleTouchEnd.bind(this))
	}
}
